import apiClient from './api.client'

export type CardType = 'UNIT' | 'SPELL' | 'ARTIFACT' | 'LEADER'
export type Faction = 'HUMAN' | 'DEMON' | 'ELF' | 'DRAGON' | 'UNDEAD' | 'NEUTRAL'
export type Rarity = 'COMMON' | 'UNCOMMON' | 'RARE' | 'LEGENDARY' | 'MYTHIC'

export interface Card {
  id: string
  cardId: string
  name: string
  cost: number
  type: CardType
  faction: Faction
  strength?: number
  goldCost?: number
  effect?: string
  skill?: string
  epicText?: string
  imageUrl?: string
  imagePath?: string
  rarity: Rarity
  edition: string
}

export interface CardFilters {
  search?: string
  type?: string
  faction?: string
  cost_min?: number
  cost_max?: number
  rarity?: string
  edition?: string
}

export interface PaginatedResponse<T> {
  success: boolean
  data: T[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export const cardsService = {
  async getCards(filters?: CardFilters, page = 1, limit = 20): Promise<PaginatedResponse<Card>> {
    const params = new URLSearchParams()
    
    if (filters?.search) params.append('search', filters.search)
    if (filters?.type) params.append('type', filters.type)
    if (filters?.faction) params.append('faction', filters.faction)
    if (filters?.cost_min) params.append('cost_min', String(filters.cost_min))
    if (filters?.cost_max) params.append('cost_max', String(filters.cost_max))
    if (filters?.rarity) params.append('rarity', filters.rarity)
    if (filters?.edition) params.append('edition', filters.edition)
    
    params.append('page', String(page))
    params.append('limit', String(limit))

    const response = await apiClient.instance.get<PaginatedResponse<Card>>(`/api/cards?${params}`)
    return response.data
  },

  async getAllCards(): Promise<Card[]> {
    const response = await apiClient.instance.get<{ success: boolean; data: Card[] }>('/api/cards?all=true')
    return response.data.data
  },

  async getCard(id: string): Promise<Card | null> {
    const response = await apiClient.instance.get<{ success: boolean; data: Card }>(`/api/cards/${id}`)
    return response.data.success ? response.data.data : null
  },

  async getCardImageUrl(id: string): Promise<string | null> {
    const response = await apiClient.instance.get<{ success: boolean; data: { imageUrl: string } }>(`/api/cards/${id}`)
    if (response.data.success && response.data.data?.imageUrl) {
      return response.data.data.imageUrl
    }
    return null
  },

  async getEditions(): Promise<string[]> {
    const response = await apiClient.instance.get<{ success: boolean; data: string[] }>('/api/cards/editions')
    return response.data.data
  },

  async getFactions(): Promise<string[]> {
    const response = await apiClient.instance.get<{ success: boolean; data: string[] }>('/api/cards/factions')
    return response.data.data
  },

  async getTypes(): Promise<string[]> {
    const response = await apiClient.instance.get<{ success: boolean; data: string[] }>('/api/cards/types')
    return response.data.data
  },

  async searchCards(q: string, limit = 10): Promise<Card[]> {
    const response = await apiClient.instance.get<{ success: boolean; data: Card[] }>(`/api/cards/search?q=${encodeURIComponent(q)}&limit=${limit}`)
    return response.data.data
  },

  async createCard(card: Omit<Card, 'id'>): Promise<Card> {
    const response = await apiClient.instance.post<{ success: boolean; data: Card }>('/api/cards', card)
    return response.data.data
  },

  async updateCard(id: string, card: Partial<Card>): Promise<void> {
    await apiClient.instance.put(`/api/cards/${id}`, card)
  },

  async deleteCard(id: string): Promise<void> {
    await apiClient.instance.delete(`/api/cards/${id}`)
  },

  async bulkImport(cards: Omit<Card, 'id'>[]): Promise<number> {
    const response = await apiClient.instance.post<{ success: boolean; message: string }>('/api/cards/bulk', cards)
    const match = response.data.message.match(/Imported (\d+) cards/)
    return match ? parseInt(match[1]) : 0
  },
}
